import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import './assets/fonts/fonts.css';
import { useEmailAuth } from './hooks/useEmailAuth';
import { useAuth } from './hooks/useAuth';
import { isAdminUser } from './utils/adminAccess';
import TabNavigation from './components/tabs/TabNavigation';
import Calendar from './components/calendar/Calendar';
import MovieCard from './components/movie/MovieCard';
import EnhancedMovieCard from './components/movie/EnhancedMovieCard';
import SearchBar from './components/common/SearchBar';
import Pagination from './components/common/Pagination';
import AnimatedBackground from './components/common/AnimatedBackground';
import ErrorBoundary from './components/common/ErrorBoundary';
import HideInterfaceToggle from './components/common/HideInterfaceToggle';
import VhsOverlay from './components/common/VhsOverlay';
import { loadThemePreference, loadThemePreferenceWithGlobal, saveThemePreference, previewThemeFromUrl } from './config/themes';
import ReviewsTab from './components/tabs/ReviewsTab';
import ViewerTab from './components/tabs/ViewerTab';
import SurveyTab from './components/tabs/SurveyTab';
import SettingsTab from './components/tabs/SettingsTab';
import TMDBListImporter from './components/movie/TMDBListImporter';
import TMDBUrlImporter from './components/movie/TMDBUrlImporter';
import PlaceholderMovieCreator from './components/movie/PlaceholderMovieCreator';
import MovieModal from './components/movie/MovieModal';
import ViewerModal from './components/viewer/ViewerModal';
import ReviewModal from './components/viewer/ReviewModal';
import { movies } from './data/movies';
import { 
  generateTimeSlots, 
  groupDatesByWeek, 
  generateDateRange, 
  calculateSlotsNeeded,
  getWeekRange
} from './utils/movieUtils';

// Import services
import { 
  loadScheduledMovies,
  loadCalendarWeeks,
  loadArchivedCalendarWeeks,
  saveScheduledMoviesForDate,
  deleteScheduledMoviesForWeek,
  saveCalendarWeek,
  deleteCalendarWeek,
} from './services/calendarService';
import { loadMovieReviews, saveMovieReview, loadMovieViewers } from './services/reviewService';
import {
  loadMovieStatuses,
  loadWeekVisibility
} from './services/appDataService';
import { 
  getAdminDefaultTabAppLevel,
  saveAdminDefaultTabAppLevel,
  saveGlobalThemeAppLevel
} from './services/appLevelSettings';
import { loadMovieCollection } from './services/movieCollectionService';
import { notifyChat } from './services/chatNotifyService';
import { toggleStarredMovie as toggleStarredMovieService, loadStarredMovies } from './services/reviewService';
import { supabase } from './config/supabase';
import { 
  startSynchronizedCountdown, 
  updateCountdownState, 
  stopSynchronizedCountdown,
  subscribeToCountdownChanges,
  unsubscribeFromCountdownChanges,
  cleanupExpiredCountdowns
} from './services/countdownSyncService';

// Development testing - load after all imports
if (process.env.NODE_ENV === 'development') {
  import('./utils/lazyLoadingTest');
}

// The countdown ticks 1..10 at one second each, so the movie starts ~10s after
// it begins. Shared with the chat notification so both agree on the moment.
const COUNTDOWN_SECONDS = 10;

// Used only to name the thing that broke in an error boundary's message.
const TAB_LABELS = {
  schedule: 'The schedule',
  collection: 'The collection',
  reviews: 'Reviews',
  viewer: 'The viewer',
  survey: 'The survey',
  settings: 'Settings'
};

export default function MovieScheduleCalendar() {
  // Single useEmailAuth call to prevent duplicate hook instances
  const { isUserAuthenticated, getCurrentUser } = useEmailAuth();
  // Separate from isUserAuthenticated, which is also true for unverified
  // "Quick Join" users. Admin requires a real, proven session - see adminAccess.
  const { isAuthenticated: hasVerifiedSession } = useAuth();
  
  // Main state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timezone, setTimezone] = useState('ET');
  const [calendarWeeks, setCalendarWeeks] = useState([]);
  const [scheduledMovies, setScheduledMovies] = useState({});
  const [draggedMovie, setDraggedMovie] = useState(null);
  const [timeSlots, setTimeSlots] = useState(() => generateTimeSlots('ET'));
  const [allMovies, setAllMovies] = useState(movies);
  const [activeTab, setActiveTab] = useState('schedule');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAlphabetically, setSortAlphabetically] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const moviesPerPage = 30;
  const [starredMovies, setStarredMovies] = useState(new Set());
  
  // Theme system
  // /?theme=<id> previews a theme for this tab only - see previewThemeFromUrl.
  const [currentTheme, setCurrentTheme] = useState(() => previewThemeFromUrl() || loadThemePreference());

  // "Hide interface": the whole interface fades out so the theme can be
  // looked at on its own. The button that brings it back stays, and so
  // does Escape. Session-only; nothing is saved.
  const [uiHidden, setUiHidden] = useState(false);
  const toggleUiHidden = useCallback(() => setUiHidden((h) => !h), []);
  
  // Admin status - controlled by admin list, not automatic
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasInitializedAdmin, setHasInitializedAdmin] = useState(false);

  // Update admin status when authentication changes.
  //
  // Gated on hasVerifiedSession, NOT isUserAuthenticated. The latter is also
  // true for "Quick Join" users, who type an address with no verification at
  // all - so it used to be enough to type the admin's address to be handed the
  // admin UI. The rule now lives in utils/adminAccess.js so it is auditable in
  // one place; note it is still only a UI gate, and RLS is the real boundary.
  useEffect(() => {
    if (!hasVerifiedSession) {
      setIsAdmin(false);
      setHasInitializedAdmin(false);
      return;
    }

    if (!hasInitializedAdmin) {
      const currentUser = getCurrentUser();
      const isUserAdmin = isAdminUser({ hasVerifiedSession, currentUser });

      console.log('Admin check:', {
        email: currentUser?.email,
        hasVerifiedSession,
        isUserAdmin
      });
      setIsAdmin(isUserAdmin);
      setHasInitializedAdmin(true);
    }
  }, [hasVerifiedSession, hasInitializedAdmin, getCurrentUser]);

  // Lazy load calendar data only when needed
  const [calendarDataLoaded, setCalendarDataLoaded] = useState(false);
  const [calendarDataLoading, setCalendarDataLoading] = useState(false);
  
  const loadCalendarData = useCallback(async () => {
    if (calendarDataLoaded || calendarDataLoading) {
      console.log('📅 Calendar data already loaded or loading, skipping...');
      return;
    }
    
    if (!isUserAuthenticated) {
      console.log('📅 User not authenticated, skipping calendar load');
      return;
    }
    
    try {
      console.log('🚀 Lazy loading calendar data...');
      setCalendarDataLoading(true);
      
      // Load scheduled movies (active only for main calendar)
      const scheduledData = await loadScheduledMovies(false); // Only active movies for main calendar
      setScheduledMovies(scheduledData);
      
      // Load archived scheduled movies separately (for reviews, etc.)
      const archivedScheduledData = await loadScheduledMovies(true); // All movies including archived
      setArchivedScheduledMovies(archivedScheduledData);
      
      // Load calendar weeks
      const weeksData = await loadCalendarWeeks(isAdmin);
      if (weeksData && weeksData.length > 0) {
        console.log('📅 Loading calendar weeks from database:', weeksData.length, 'weeks');
        weeksData.forEach(weekData => {
          console.log(`📅 Week: ${weekData.week_id} (${weekData.year}) - ${weekData.start_date} to ${weekData.end_date}`);
        });
        
        const weeks = weeksData.map(weekData => 
          weekData.dates.map(dateStr => {
            // Parse date components directly to avoid any timezone interpretation
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
          })
        );
        setCalendarWeeks(weeks);
      }
      
      // Load archived weeks for archive functionality
      console.log('🔄 Loading archived calendar weeks...');
      const archivedWeeksData = await loadArchivedCalendarWeeks(isAdmin);
      console.log('📊 Archived weeks loaded:', archivedWeeksData.length, 'weeks');
      if (archivedWeeksData.length > 0) {
        console.log('📋 First archived week sample:', archivedWeeksData[0]);
      }
      setArchivedWeeks(archivedWeeksData);
      
      setCalendarLoaded(true);
      setCalendarDataLoaded(true);
      console.log('✅ Calendar data lazy loading complete');
    } catch (error) {
      console.error('❌ Error loading calendar data:', error);
      setCalendarLoaded(true); // Still set to true to prevent infinite loading
    } finally {
      setCalendarDataLoading(false);
    }
  }, [isAdmin, calendarDataLoaded, calendarDataLoading, isUserAuthenticated]);

  // Load app-level data from Supabase
  useEffect(() => {
    const loadAppData = async () => {
      try {
        console.log('🔄 Loading app-level data from Supabase...');
        
        // Load movie statuses
        const statuses = await loadMovieStatuses();
        setMovieStatuses(statuses);
        
        // Load week visibility
        const visibility = await loadWeekVisibility();
        setViewableWeeks(new Set(visibility));
        
        // Admin preferences are loaded separately in their own useEffect
        
        setAppDataLoaded(true);
        console.log('✅ App-level data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading app-level data:', error);
        setAppDataLoaded(true); // Still mark as loaded to avoid infinite loading
      }
    };

    if (isUserAuthenticated) {
      loadAppData();
    }
  }, [isUserAuthenticated, isAdmin]);

  // Load reviews data from Supabase
  useEffect(() => {
    const loadReviewsData = async () => {
      try {
        console.log('🔄 Loading movie reviews from Supabase...');
        
        const reviews = await loadMovieReviews();
        setMovieReviews(reviews);
        
        setReviewsLoaded(true);
        console.log('✅ Reviews data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading reviews data:', error);
        setReviewsLoaded(true); // Still mark as loaded to avoid infinite loading
      }
    };

    if (isUserAuthenticated) {
      loadReviewsData();
    }
  }, [isUserAuthenticated]);

  // Load and sync admin default tab setting
  useEffect(() => {
    const loadAndSyncDefaultTab = async () => {
      if (!isAdmin) return; // Only load for admin users
      
      try {
        // Get current values from both sources
        const localDefaultTab = localStorage.getItem('admin_default_tab') || 'viewer';
        const supabaseResult = await getAdminDefaultTabAppLevel();
        const supabaseDefaultTab = supabaseResult.data || 'viewer';
        
        // If they match, use the value
        if (localDefaultTab === supabaseDefaultTab) {
          setDefaultTab(localDefaultTab);
          console.log('🔄 Admin default tab already synced:', localDefaultTab);
          return;
        }
        
        // If they don't match, prioritize localStorage (more recent user action)
        // and update Supabase to match
        console.log('🔄 Admin default tab mismatch - localStorage:', localDefaultTab, 'Supabase:', supabaseDefaultTab);
        console.log('🔄 Prioritizing localStorage value and syncing to Supabase');
        
        setDefaultTab(localDefaultTab);
        await saveAdminDefaultTabAppLevel(localDefaultTab);
        console.log('✅ Admin default tab synced localStorage → Supabase:', localDefaultTab);
        
      } catch (error) {
        console.error('❌ Error loading admin default tab:', error);
        // Fallback to localStorage if Supabase fails
        const localDefaultTab = localStorage.getItem('admin_default_tab') || 'viewer';
        setDefaultTab(localDefaultTab);
        console.log('🔄 Fallback to localStorage admin default tab:', localDefaultTab);
      }
    };
    
    loadAndSyncDefaultTab();
  }, [isAdmin]); // Run when admin status changes

  // Load global theme setting on app startup
  useEffect(() => {
    const loadGlobalTheme = async () => {
      // A theme named in the URL is being previewed; the global one would
      // only override it a moment after the page appeared.
      if (previewThemeFromUrl()) return;
      try {
        const globalTheme = await loadThemePreferenceWithGlobal();
        if (globalTheme && globalTheme.id !== currentTheme.id) {
          console.log('🎨 Loading global theme:', globalTheme.name);
          setCurrentTheme(globalTheme);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load global theme, using current theme:', error);
      }
    };

    loadGlobalTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, []); // Run once on app startup

  // Load viewers data and set up real-time subscriptions
  useEffect(() => {
    let subscription = null;

    const loadViewersData = async () => {
      try {
        console.log('🔄 Loading movie viewers from Supabase...');
        
        const viewers = await loadMovieViewers();
        setMovieViewers(viewers);
        
        console.log('✅ Viewers data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading viewers data:', error);
      }
    };

    const setupRealtimeSubscription = () => {
      if (!supabase) {
        console.log('⚠️ Supabase not configured, skipping real-time subscription');
        return;
      }

      console.log('🔄 Setting up real-time subscription for movie_viewers...');
      
      subscription = supabase
        .channel('movie_viewers_realtime')
        .on('postgres_changes', {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'movie_viewers'
        }, (payload) => {
          console.log('📡 Real-time viewer update:', payload);
          handleViewerRealtimeUpdate(payload);
        })
        .subscribe();

      console.log('✅ Real-time subscription established');
    };

    if (isUserAuthenticated) {
      loadViewersData();
      setupRealtimeSubscription();
    }

    // Cleanup subscription on unmount
    return () => {
      if (subscription) {
        console.log('🧹 Cleaning up real-time subscription');
        subscription.unsubscribe();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [isUserAuthenticated]);

  // Refresh viewer data from Supabase
  const refreshViewerData = async () => {
    try {
      console.log('🔄 Refreshing movie viewers from Supabase...');
      const viewers = await loadMovieViewers();
      setMovieViewers(viewers);
      console.log('✅ Viewer data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing viewer data:', error);
    }
  };

  // Handle real-time viewer updates
  const handleViewerRealtimeUpdate = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    setMovieViewers(prev => {
      const updated = { ...prev };
      
      if (eventType === 'INSERT' && newRecord) {
        // Add new viewer
        const movieId = newRecord.movie_id;
        if (!updated[movieId]) {
          updated[movieId] = [];
        }
        
        // Check if viewer already exists (avoid duplicates)
        const existingIndex = updated[movieId].findIndex(v => v.name === newRecord.viewer_name);
        if (existingIndex === -1) {
          updated[movieId].push({
            name: newRecord.viewer_name,
            status: newRecord.status,
            ready: newRecord.status === 'ready',
            joinedAt: newRecord.created_at
          });
          console.log(`✅ Added viewer: ${newRecord.viewer_name} to ${movieId}`);
        }
      }
      
      else if (eventType === 'UPDATE' && newRecord) {
        // Update existing viewer
        const movieId = newRecord.movie_id;
        if (updated[movieId]) {
          const viewerIndex = updated[movieId].findIndex(v => v.name === newRecord.viewer_name);
          if (viewerIndex !== -1) {
            updated[movieId][viewerIndex] = {
              name: newRecord.viewer_name,
              status: newRecord.status,
              ready: newRecord.status === 'ready',
              joinedAt: newRecord.created_at
            };
            console.log(`✅ Updated viewer: ${newRecord.viewer_name} in ${movieId} -> ${newRecord.status}`);
          }
        }
      }
      
      else if (eventType === 'DELETE' && oldRecord) {
        // DELETE events from Supabase only contain the ID, not the full record data
        // Since we handle local state updates immediately when users leave,
        // we'll refresh the viewer list to ensure consistency
        console.log('🗑️ Real-time DELETE event received - refreshing viewer data');
        console.log('🔍 DELETE event payload:', { id: oldRecord.id });
        
        // Refresh viewer data from Supabase to ensure consistency
        // This is more reliable than trying to parse incomplete DELETE data
        refreshViewerData();
      }
      
      return updated;
    });
  };

  // Load movie collection from Supabase
  useEffect(() => {
    const loadMovieCollectionData = async () => {
      try {
        console.log('🔄 Loading movie collection from Supabase...');
        
        const movies = await loadMovieCollection();
        if (movies && movies.length > 0) {
          setAllMovies(movies);
          console.log('✅ Movie collection loaded successfully:', movies.length, 'movies');
          
          // TMDB data will be loaded lazily when movies become visible
          console.log('📋 TMDB data will be loaded on-demand via lazy loading');
        } else {
          console.log('📝 No movies found in collection, using default movies');
          // Keep the default movies from import
        }
      } catch (error) {
        console.error('❌ Error loading movie collection:', error);
        // Keep the default movies from import
      }
    };

    // Load movie collection on app startup (doesn't require authentication)
    loadMovieCollectionData();
  }, []); // Only run once on startup

  // Load starred movies when user authenticates
  useEffect(() => {
    const loadStarredMoviesData = async () => {
      try {
        const currentUser = getCurrentUser();
        if (!currentUser?.email) {
          console.log('📝 No user email, skipping starred movies load');
          return;
        }

        console.log('🔄 Loading starred movies from Supabase...');
        const starredSet = await loadStarredMovies(currentUser.email);
        setStarredMovies(starredSet);
        console.log('✅ Starred movies loaded successfully:', starredSet.size, 'movies');
      } catch (error) {
        console.error('❌ Error loading starred movies:', error);
      }
    };

    if (isUserAuthenticated) {
      loadStarredMoviesData();
    }
  }, [isUserAuthenticated, getCurrentUser]);

  // Set up countdown subscriptions for scheduled movies
  useEffect(() => {
    const setupCountdownSubscriptions = async () => {
      // Clean up expired countdowns first
      await cleanupExpiredCountdowns();
      
      // Subscribe to countdown changes for all scheduled movies
      Object.values(scheduledMovies).forEach(dateMovies => {
        Object.values(dateMovies).forEach(slotMovies => {
          Object.values(slotMovies).forEach(movie => {
            if (movie && movie.id) {
              // Inline subscription logic to avoid circular dependency
              if (!countdownSubscriptions.has(movie.id)) {
                console.log('👂 Subscribing to countdown changes for movie:', movie.id);
                
                const subscription = subscribeToCountdownChanges(movie.id, (countdownData) => {
                  if (countdownData) {
                    console.log('🔄 Received countdown update from subscription:', countdownData);
                    // Only update local state if we're not currently running our own countdown for this movie
                    setCountdown(prev => {
                      // Skip real-time updates ONLY if we're the admin running our own local countdown
                      if (prev && prev.movieId === countdownData.movieId && isAdmin && countdownInterval) {
                        console.log('⏭️ Skipping subscription update - admin running local countdown');
                        return prev;
                      }
                      console.log('✅ Updating countdown from subscription:', countdownData);
                      return {
                        movieId: countdownData.movieId,
                        seconds: countdownData.seconds,
                        phase: countdownData.phase
                      };
                    });
                  } else {
                    console.log('🛑 Countdown stopped for movie');
                    // Clear countdown (since countdownData is null, we don't have movieId)
                    setCountdown(null);
                  }
                });
                
                if (subscription) {
                  setCountdownSubscriptions(prev => new Map(prev).set(movie.id, subscription));
                }
              }
            }
          });
        });
      });
    };
    
    if (Object.keys(scheduledMovies).length > 0) {
      setupCountdownSubscriptions();
    }
    
    // Cleanup subscriptions on unmount
    return () => {
      countdownSubscriptions.forEach((subscription, movieId) => {
        unsubscribeFromCountdownChanges(subscription);
      });
      setCountdownSubscriptions(new Map());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [scheduledMovies]);

  // Admin preferences
  const [defaultTab, setDefaultTab] = useState(() => {
    return localStorage.getItem('admin_default_tab') || 'viewer';
  });
  const [countdownImages, setCountdownImages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('countdown_images') || '[]');
    } catch (error) {
      console.error('Error loading countdown images:', error);
      return [];
    }
  });
  
  // Handle admin state changes
  const handleAdminChange = (newIsAdmin) => {
    setIsAdmin(newIsAdmin);
    // If switching to non-admin mode, only switch to default tab if currently on an admin-only tab
    if (!newIsAdmin) {
      const adminOnlyTabs = ['schedule', 'collection'];
      if (adminOnlyTabs.includes(activeTab)) {
        setActiveTab(defaultTab);
      }
      // If already on a non-admin tab ('viewer', 'reviews', 'survey', or 'settings'), stay there
    }
  };

  const [showTMDBListImporter, setShowTMDBListImporter] = useState(false);
  const [showTMDBUrlImporter, setShowTMDBUrlImporter] = useState(false);
  const [showPlaceholderCreator, setShowPlaceholderCreator] = useState(false);
  
  // Archive functionality states
  const [archivedWeeks, setArchivedWeeks] = useState([]);
  
  // Week visibility states
  const [viewableWeeks, setViewableWeeks] = useState(new Set()); // Track which weeks are viewable by public
  
  // Data loading states (kept for compatibility with lazy loading)
  const [, setCalendarLoaded] = useState(false);
  const [, setReviewsLoaded] = useState(false);
  const [, setAppDataLoaded] = useState(false);
  const [archivedScheduledMovies, setArchivedScheduledMovies] = useState({});
  
  // Modal states
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [selectedViewerMovie, setSelectedViewerMovie] = useState(null);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingMovieId, setReviewingMovieId] = useState(null);
  
  // Viewer functionality states
  const [movieStatuses, setMovieStatuses] = useState({});
  const [movieViewers, setMovieViewers] = useState({});
  const [movieReviews, setMovieReviews] = useState({});
  const [userName, setUserName] = useState('');
  const [countdown, setCountdown] = useState(null); // { movieId, seconds, phase }
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [countdownSubscriptions, setCountdownSubscriptions] = useState(new Map()); // Track subscriptions by movieId
  
  // Timezone options
  const timezones = [
    { value: 'ET', label: 'Eastern Time (ET)' },
    { value: 'CT', label: 'Central Time (CT)' },
    { value: 'MT', label: 'Mountain Time (MT)' },
    { value: 'PT', label: 'Pacific Time (PT)' }
  ];

  // Calendar generation
  const generateCalendar = async () => {
    if (!startDate || !endDate) return;

    const dates = generateDateRange(startDate, endDate);
    const newWeeks = groupDatesByWeek(dates);
    
    // Merge with existing weeks and save new ones to Supabase
    // Process new weeks and check for conflicts before updating state
    const allWeeks = [...calendarWeeks];
    const weeksToSave = [];
    
    // Add new weeks that don't overlap with existing ones
    // Process each new week and check for database conflicts
    for (const newWeek of newWeeks) {
        const newWeekKey = newWeek.map(date => date.toISOString().split('T')[0]).join('-');
        
        // Check if this week already exists in local state
        const existingWeekIndex = allWeeks.findIndex(existingWeek => {
          const existingWeekKey = existingWeek.map(date => date.toISOString().split('T')[0]).join('-');
          return existingWeekKey === newWeekKey;
        });
        
        if (existingWeekIndex === -1) {
          // Week doesn't exist in local state, safe to add
          allWeeks.push(newWeek);
          
          // Prepare week data for Supabase
          const weekStart = newWeek[0];
          const weekEnd = newWeek[newWeek.length - 1];
          const weekId = `week-${weekStart.toISOString().split('T')[0]}-${weekEnd.toISOString().split('T')[0]}`;
          
          const getWeekNumber = (date) => {
            const start = new Date(date.getFullYear(), 0, 1);
            const days = Math.floor((date - start) / (24 * 60 * 60 * 1000));
            return Math.ceil((days + start.getDay() + 1) / 7);
          };
          
          const calendarWeekData = {
            id: weekId,
            title: `Week of ${weekStart.toLocaleDateString()}`,
            start_date: weekStart.toISOString().split('T')[0],
            end_date: weekEnd.toISOString().split('T')[0],
            dates: newWeek.map(date => date.toISOString().split('T')[0]),
            year: weekStart.getFullYear(),
            weekNumber: getWeekNumber(weekStart),
            isArchived: false,
            isHidden: false
          };
          
          weeksToSave.push(calendarWeekData);
          console.log(`✅ New week prepared for creation: ${weekId} (${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()})`);
        } else {
          console.log(`⚠️ Week already exists in local state, skipping: ${newWeekKey}`);
        }
        // If week exists, keep the existing one (don't replace)
      }
      
    // Save new weeks to Supabase sequentially (let saveCalendarWeek handle duplicates)
    if (weeksToSave.length > 0) {
      console.log('💾 Saving', weeksToSave.length, 'new calendar weeks to Supabase...');
      
      // Process weeks sequentially to avoid race conditions
      for (const weekData of weeksToSave) {
        try {
          console.log('💾 Attempting to save calendar week:', weekData.id);
          const result = await saveCalendarWeek(weekData);
          if (result.error) {
            console.error('❌ Error saving calendar week:', weekData.id, result.error);
          } else {
            console.log('✅ Calendar week saved to Supabase:', weekData.id);
          }
        } catch (error) {
          console.error('❌ Exception saving calendar week:', weekData.id, error);
        }
      }
    }
    
    // Sort weeks by start date and update state
    const sortedWeeks = allWeeks.sort((a, b) => a[0].getTime() - b[0].getTime());
    setCalendarWeeks(sortedWeeks);
  };

  // Timezone handling
  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone);
    setTimeSlots(generateTimeSlots(newTimezone));
  };

  // Movie management
  const getAvailableMovies = () => {
    return allMovies.filter(movie => starredMovies.has(movie.id));
  };

  const toggleStarredMovie = async (movieId) => {
    const currentUser = getCurrentUser();
    const userIdentifier = currentUser?.email || 'anonymous';
    const isCurrentlyStarred = starredMovies.has(movieId);
    const newIsStarred = !isCurrentlyStarred;

    // Update local state immediately for responsive UI
    setStarredMovies(prev => {
      const newStarred = new Set(prev);
      if (newIsStarred) {
        newStarred.add(movieId);
      } else {
        newStarred.delete(movieId);
      }
      return newStarred;
    });

    // Persist to Supabase
    try {
      const result = await toggleStarredMovieService(movieId, userIdentifier, newIsStarred);
      if (result.error) {
        console.error('❌ Failed to persist starred movie change:', result.error);
        // Revert local state on error
        setStarredMovies(prev => {
          const revertedStarred = new Set(prev);
          if (isCurrentlyStarred) {
            revertedStarred.add(movieId);
          } else {
            revertedStarred.delete(movieId);
          }
          return revertedStarred;
        });
      } else {
        console.log('✅ Starred movie change persisted to Supabase');
      }
    } catch (error) {
      console.error('❌ Error persisting starred movie change:', error);
      // Revert local state on error
      setStarredMovies(prev => {
        const revertedStarred = new Set(prev);
        if (isCurrentlyStarred) {
          revertedStarred.add(movieId);
        } else {
          revertedStarred.delete(movieId);
        }
        return revertedStarred;
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, movie) => {
    setDraggedMovie(movie);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, date, timeSlot) => {
    e.preventDefault();
    if (!draggedMovie) return;

    const dateStr = date.toISOString().split('T')[0];
    const slotsNeeded = calculateSlotsNeeded(draggedMovie.runtime);
    
    // Check if there's enough consecutive time slots
    const timeSlotIndex = timeSlots.findIndex(slot => slot.id === timeSlot.id);
    if (timeSlotIndex + slotsNeeded > timeSlots.length) {
      alert('Not enough time slots for this movie!');
      setDraggedMovie(null);
      return;
    }

    // Check for conflicts (but ignore the movie being moved if it's the same one)
    const currentSchedule = scheduledMovies[dateStr] || [];
    for (let i = 0; i < slotsNeeded; i++) {
      const checkSlot = timeSlots[timeSlotIndex + i];
      const conflict = currentSchedule.find(scheduled => 
        scheduled.timeSlot.id === checkSlot.id && scheduled.movie.id !== draggedMovie.id
      );
      if (conflict) {
        alert(`Conflict with ${conflict.movie.title} at ${checkSlot.time}!`);
        setDraggedMovie(null);
        return;
      }
    }

    // If this is a move operation (movie is already scheduled), remove it from its old position first
    if (draggedMovie.isScheduled) {
      setScheduledMovies(prev => {
        const newScheduled = { ...prev };
        Object.keys(newScheduled).forEach(key => {
          newScheduled[key] = newScheduled[key].filter(scheduled => 
            scheduled.movie.id !== draggedMovie.id
          );
        });
        return newScheduled;
      });
    }
    
    // Create scheduled movie entry
    const scheduledMovie = {
      id: `${draggedMovie.id}-${dateStr}-${timeSlot.id}`,
      movie: draggedMovie,
      date: dateStr,
      timeSlot: timeSlot,
      slotsSpanned: slotsNeeded
    };

    // Calculate what the new scheduled movies will be for this date
    const existingMoviesForDate = scheduledMovies[dateStr] || [];
    const filteredMovies = existingMoviesForDate.filter(s => s.movie.id !== draggedMovie.id);
    const newMoviesForDate = [...filteredMovies, scheduledMovie];

    // Add to the new position (filter out any existing instance to prevent duplication)
    setScheduledMovies(prev => ({
      ...prev,
      [dateStr]: newMoviesForDate
    }));

    // Persist to Supabase (single operation, outside state updater)
    saveScheduledMoviesForDate(dateStr, newMoviesForDate).then(result => {
      if (result.error) {
        console.error('❌ Failed to save scheduled movies to Supabase:', result.error);
      } else {
        console.log('✅ Scheduled movies saved to Supabase for', dateStr);
      }
    }).catch(error => {
      console.error('❌ Error saving scheduled movies:', error);
    });

    // Auto-unstar the movie only when first scheduling (not when moving)
    if (starredMovies.has(draggedMovie.id) && !draggedMovie.isScheduled) {
      toggleStarredMovie(draggedMovie.id);
    }

    setDraggedMovie(null);
  };

  // Utility functions for calendar
  const getScheduledMovieForSlot = (date, timeSlot) => {
    // Use timezone-agnostic date string conversion to avoid UTC shift
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const dayMovies = scheduledMovies[dateStr] || [];
    
    for (const movie of dayMovies) {
      const movieStartIndex = timeSlots.findIndex(ts => ts.id === movie.timeSlot.id);
      const slotIndex = timeSlots.findIndex(ts => ts.id === timeSlot.id);
      
      if (slotIndex >= movieStartIndex && slotIndex < movieStartIndex + movie.slotsSpanned) {
        return movie;
      }
    }
    return null;
  };

  const isSlotOccupied = (date, timeSlot) => {
    return getScheduledMovieForSlot(date, timeSlot) !== null;
  };

  const removeScheduledMovie = async (dateStr, movieId) => {
    // Update local state immediately for responsive UI
    setScheduledMovies(prev => {
      const updated = {
        ...prev,
        [dateStr]: (prev[dateStr] || []).filter(movie => movie.id !== movieId)
      };
      
      // Persist the updated movies for this date to Supabase
      const updatedMoviesForDate = updated[dateStr] || [];
      saveScheduledMoviesForDate(dateStr, updatedMoviesForDate).then(result => {
        if (result.error) {
          console.error('❌ Failed to save updated schedule to Supabase:', result.error);
        } else {
          console.log(`✅ Removed movie ${movieId} from ${dateStr} and updated Supabase`);
        }
      });
      
      return updated;
    });
  };

  // Filtered movies for collection view
  const getFilteredAndSortedMovies = () => {
    let filtered = allMovies;
    
    if (searchTerm.trim()) {
      filtered = filtered.filter(movie => 
        movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movie.year.toString().includes(searchTerm)
      );
    }

    if (sortAlphabetically) {
      filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
    }

    return filtered;
  };

  // Pagination helper functions
  const getPaginatedMovies = () => {
    const allFilteredMovies = getFilteredAndSortedMovies();
    const startIndex = (currentPage - 1) * moviesPerPage;
    const endIndex = startIndex + moviesPerPage;
    return allFilteredMovies.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const totalMovies = getFilteredAndSortedMovies().length;
    return Math.ceil(totalMovies / moviesPerPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of movie grid when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset pagination when search or sort changes
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSortChange = (newSortValue) => {
    setSortAlphabetically(newSortValue);
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get streaming service by ID
  const getStreamingServiceById = (id) => {
    // Default streaming services
    const defaultServices = [
      { id: 'netflix', name: 'Netflix', color: 'bg-red-600', icon: '🎬' },
      { id: 'amazon', name: 'Amazon Prime', color: 'bg-blue-600', icon: '📺' },
      { id: 'disney', name: 'Disney+', color: 'bg-blue-800', icon: '🏰' },
      { id: 'hulu', name: 'Hulu', color: 'bg-green-600', icon: '🎭' },
      { id: 'hbo', name: 'HBO Max', color: 'bg-purple-700', icon: '👑' },
      { id: 'apple', name: 'Apple TV+', color: 'bg-gray-800', icon: '🍎' },
      { id: 'paramount', name: 'Paramount+', color: 'bg-blue-500', icon: '⭐' },
      { id: 'peacock', name: 'Peacock', color: 'bg-purple-600', icon: '🦚' },
      { id: 'showtime', name: 'Showtime', color: 'bg-red-800', icon: '🎪' },
      { id: 'starz', name: 'Starz', color: 'bg-black', icon: '✨' },
      { id: 'free', name: 'Free (Tubi/Crackle)', color: 'bg-green-700', icon: '🆓' },
      { id: 'rent', name: 'Digital Rental', color: 'bg-orange-700', icon: '💰' },
      { id: 'physical', name: 'Physical Media', color: 'bg-gray-700', icon: '💿' },
      { id: 'theater', name: 'In Theaters', color: 'bg-yellow-600', icon: '🎫' },
      // TMDB services (with logos)
      { id: 'tmdb_netflix', name: 'Netflix', logo: 'https://image.tmdb.org/t/p/original/pbpMk2JmcoNnQwx5JGpXngfoWtp.jpg', color: 'bg-red-600' },
      { id: 'tmdb_amazon_prime', name: 'Amazon Prime Video', logo: 'https://image.tmdb.org/t/p/original/68MNrwlkpF7WnmNPXLah69CR5cb.jpg', color: 'bg-blue-600' },
      { id: 'tmdb_shudder', name: 'Shudder', logo: 'https://image.tmdb.org/t/p/original/5VISkzLZOA1UpzAdVlRPtBXpjI.jpg', color: 'bg-red-900' },
      { id: 'tmdb_disney_plus', name: 'Disney Plus', logo: 'https://image.tmdb.org/t/p/original/7rwgEs15tFwyR9NPQ5vpzxTj19Q.jpg', color: 'bg-blue-800' },
      { id: 'tmdb_hbo_max', name: 'HBO Max', logo: 'https://image.tmdb.org/t/p/original/Ajqyt5aNxNGjmF9uOfxArGrdf3X.jpg', color: 'bg-purple-700' },
      { id: 'tmdb_max', name: 'Max', logo: 'https://image.tmdb.org/t/p/original/Ajqyt5aNxNGjmF9uOfxArGrdf3X.jpg', color: 'bg-purple-700' },
      { id: 'tmdb_peacock', name: 'Peacock', logo: 'https://image.tmdb.org/t/p/original/xTVM8uXT9QocigQ08cLMNECZcQB.jpg', color: 'bg-purple-600' },
      { id: 'tmdb_paramount_plus', name: 'Paramount+', logo: 'https://image.tmdb.org/t/p/original/h5DcR0J2EESLitnhR8xLG1QymTE.jpg', color: 'bg-blue-500' },
      { id: 'tmdb_kanopy', name: 'Kanopy', logo: 'https://image.tmdb.org/t/p/original/sVBEF7q7LqjHAWSnKwDbzmr2EMY.jpg', color: 'bg-yellow-600' },
      { id: 'tmdb_criterion', name: 'Criterion Channel', logo: 'https://image.tmdb.org/t/p/original/da3jxo6RdnZZweAgdx4R9YNqHFj.jpg', color: 'bg-black' },
      { id: 'tmdb_amazon_video', name: 'Amazon Video', logo: 'https://image.tmdb.org/t/p/original/68MNrwlkpF7WnmNPXLah69CR5cb.jpg', color: 'bg-orange-600' },
      { id: 'tmdb_hulu', name: 'Hulu', logo: 'https://image.tmdb.org/t/p/original/pqUTCoBcQAGyM93Z515dLovOAKe.jpg', color: 'bg-green-600' },
      { id: 'tmdb_apple_tv', name: 'Apple TV+', logo: 'https://image.tmdb.org/t/p/original/2E03IAZsX4ZaUqM7tXlctEPMGWS.jpg', color: 'bg-gray-800' },
      { id: 'tmdb_showtime', name: 'Showtime', logo: 'https://image.tmdb.org/t/p/original/SvvG9ZIdrjkqLnkTLDelgPEXvP.jpg', color: 'bg-red-800' },
      { id: 'tmdb_starz', name: 'Starz', logo: 'https://image.tmdb.org/t/p/original/5L8VQcCEDC9bD7gpuMWKJnTRz9P.jpg', color: 'bg-black' },
      // Additional TMDB services
      { id: 'tmdb_apple_itunes', name: 'Apple iTunes', icon: '🍎', color: 'bg-gray-700' },
      { id: 'tmdb_google_play', name: 'Google Play Movies', icon: '🎞️', color: 'bg-blue-500' },
      { id: 'tmdb_microsoft', name: 'Microsoft Store', icon: '🪟', color: 'bg-blue-600' },
      { id: 'tmdb_vudu', name: 'Vudu', icon: '🎬', color: 'bg-blue-700' },
      { id: 'tmdb_tubi', name: 'Tubi TV', icon: '🆓', color: 'bg-green-700' },
      { id: 'tmdb_crackle', name: 'Crackle', icon: '🆓', color: 'bg-orange-700' },
      { id: 'tmdb_pluto', name: 'Pluto TV', icon: '🪐', color: 'bg-purple-500' },
      { id: 'tmdb_hbo_now', name: 'HBO Now', icon: '👑', color: 'bg-purple-800' },
      { id: 'tmdb_discovery', name: 'Discovery+', icon: '🔍', color: 'bg-blue-400' },
      { id: 'tmdb_amazon_prime_uk', name: 'Amazon Prime Video (UK)', icon: '📺', color: 'bg-blue-600' },
      { id: 'tmdb_netflix_kids', name: 'Netflix Kids', icon: '👶', color: 'bg-red-400' },
      { id: 'tmdb_youtube_tv', name: 'YouTube TV', icon: '📺', color: 'bg-red-500' },
      { id: 'tmdb_fubotv', name: 'fuboTV', icon: '⚽', color: 'bg-green-500' },
      { id: 'tmdb_epix', name: 'Epix', icon: '🎬', color: 'bg-yellow-600' },
      { id: 'tmdb_netflix_basic', name: 'Netflix basic with Ads', icon: '🎬', color: 'bg-red-500' }
    ];
    
    return defaultServices.find(service => service.id === id) || null;
  };



  // Modal handlers
  const openMovieModal = (movie) => {
    setSelectedMovie(movie);
    setShowMovieModal(true);
  };

  const closeMovieModal = () => {
    setSelectedMovie(null);
    setShowMovieModal(false);
  };

  const openViewerModal = (scheduledMovie) => {
    setSelectedViewerMovie(scheduledMovie);
    setShowViewerModal(true);
  };

  const closeViewerModal = () => {
    setSelectedViewerMovie(null);
    setShowViewerModal(false);
  };

  const openReviewModal = (movieId) => {
    setReviewingMovieId(movieId);
    setShowReviewModal(true);
  };

  // Arriving from a "write a review" prompt in chat: /?review=<movieId>.
  // The app has no router, so the parameter is read once at startup.
  const [pendingReviewId, setPendingReviewId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('review');
  });

  // Take ?review= out of the address bar immediately. It is captured in state
  // above, and leaving it there would reopen the form on every refresh.
  useEffect(() => {
    if (pendingReviewId && typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Mount only. pendingReviewId is never set again after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // allMovies starts life as the bundled seed list and is only replaced once
  // the collection arrives from Supabase, so a real id cannot resolve on the
  // first pass and "not found yet" is not the same as "wrong id". Keep trying
  // as movie data changes, and give up after a while rather than springing the
  // review form open long after someone has moved on.
  useEffect(() => {
    if (!pendingReviewId) return undefined;

    // The parameter is a string, but some movies carry numeric ids and
    // findMovieById compares with ===.
    const movie = findMovieById(pendingReviewId)
      || (/^\d+$/.test(pendingReviewId) ? findMovieById(Number(pendingReviewId)) : null);

    if (movie) {
      openReviewModal(movie.id);
      setPendingReviewId(null);
      return undefined;
    }

    const giveUp = setTimeout(() => setPendingReviewId(null), 15000);
    return () => clearTimeout(giveUp);
    // findMovieById and openReviewModal are rebuilt every render; depending on
    // them would restart this on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReviewId, allMovies, scheduledMovies, archivedScheduledMovies]);

  const closeReviewModal = () => {
    setReviewingMovieId(null);
    setShowReviewModal(false);
  };

  // Find movie by ID in both allMovies and scheduledMovies (including archived)
  const findMovieById = (movieId) => {
    // First try to find in allMovies (base movie data)
    const baseMovie = allMovies.find(m => m.id === movieId);
    if (baseMovie) {
      return baseMovie;
    }
    
    // Search in active scheduled movies
    for (const dateMovies of Object.values(scheduledMovies)) {
      for (const scheduledMovie of dateMovies) {
        if (scheduledMovie.movie && scheduledMovie.movie.id === movieId) {
          return scheduledMovie.movie;
        }
      }
    }
    
    // Search in archived scheduled movies
    for (const dateMovies of Object.values(archivedScheduledMovies)) {
      for (const scheduledMovie of dateMovies) {
        if (scheduledMovie.movie && scheduledMovie.movie.id === movieId) {
          return scheduledMovie.movie;
        }
      }
    }
    
    console.warn('⚠️ Movie not found by ID:', movieId);
    return null;
  };

  // Streaming service management
  const updateMovieStreamingServices = (movieId, services, preferredService) => {
    // Update the movie in allMovies
    setAllMovies(prev => prev.map(movie => {
      if (movie.id === movieId) {
        return {
          ...movie,
          streamingServices: services || [],
          preferredStreamingService: preferredService
        };
      }
      return movie;
    }));

    // Also update any scheduled movies that reference this movie
    setScheduledMovies(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      Object.keys(updated).forEach(dateKey => {
        updated[dateKey] = updated[dateKey].map(scheduledMovie => {
          if (scheduledMovie.movie.id === movieId) {
            hasChanges = true;
            return {
              ...scheduledMovie,
              movie: {
                ...scheduledMovie.movie,
                streamingServices: services || [],
                preferredStreamingService: preferredService
              }
            };
          }
          return scheduledMovie;
        });
      });

      return hasChanges ? updated : prev;
    });
  };

  // Viewer functionality
  const setMovieStatus = async (movieId, status) => {
    // Update local state immediately for responsive UI
    setMovieStatuses(prev => ({
      ...prev,
      [movieId]: status
    }));

    // Persist to Supabase
    try {
      const { setMovieStatus: setMovieStatusService } = await import('./services/appDataService');
      const currentUser = getCurrentUser();
      const updatedBy = currentUser?.email || 'admin';
      
      const result = await setMovieStatusService(movieId, status, updatedBy);
      if (result.error) {
        console.error('❌ Failed to persist movie status change:', result.error);
        // Could revert local state here if needed
      } else {
        console.log('✅ Movie status change persisted to Supabase:', movieId, status);
      }
    } catch (error) {
      console.error('❌ Error persisting movie status change:', error);
    }
  };

  const getMovieStatus = (movieId) => {
    return movieStatuses[movieId] || 'coming';
  };

  const joinWatching = async (movieId, name) => {
    if (!name.trim()) return;
    
    const trimmedName = name.trim();
    
    // Update local state immediately for responsive UI
    setMovieViewers(prev => ({
      ...prev,
      [movieId]: [...(prev[movieId] || []), { name: trimmedName, ready: false }]
    }));
    setUserName(trimmedName);

    // Persist to Supabase
    try {
      const { saveMovieViewer } = await import('./services/reviewService');
      const result = await saveMovieViewer(movieId, trimmedName, 'watching');
      if (result.error) {
        console.error('❌ Failed to persist viewer join:', result.error);
      } else {
        console.log('✅ Viewer join persisted to Supabase:', movieId, trimmedName);
      }
    } catch (error) {
      console.error('❌ Error persisting viewer join:', error);
    }
  };

  const leaveWatching = async (movieId, name) => {
    console.log('🚪 leaveWatching called:', { movieId, name });
    console.log('🔍 Current viewers before leaving:', movieViewers[movieId]);
    
    // Update local state immediately for responsive UI
    setMovieViewers(prev => {
      const updated = {
        ...prev,
        [movieId]: (prev[movieId] || []).filter(viewer => viewer.name !== name)
      };
      console.log('🔄 Local state updated, new viewers:', updated[movieId]);
      return updated;
    });

    // Remove from Supabase
    try {
      console.log('🗑️ Attempting to delete viewer from Supabase...');
      const { queuedSupabaseOperation } = await import('./utils/supabaseWrapper');
      const result = await queuedSupabaseOperation.delete(
        'movie_viewers',
        `movie_id=eq.${encodeURIComponent(movieId)}&viewer_name=eq.${encodeURIComponent(name)}`,
        null, // No fallback
        `Remove viewer: ${movieId} - ${name}`
      );
      console.log('✅ Viewer removal persisted to Supabase:', movieId, name, 'Rows deleted:', result?.length || 0);
      
      if (!result || result.length === 0) {
        console.warn('⚠️ No rows were deleted - viewer may not have existed in database');
      }
    } catch (error) {
      console.error('❌ Error persisting viewer removal:', error);
      
      // If Supabase delete failed, revert local state
      setMovieViewers(prev => {
        const reverted = { ...prev };
        if (!reverted[movieId]) {
          reverted[movieId] = [];
        }
        // Add the viewer back if they're not already there
        if (!reverted[movieId].find(v => v.name === name)) {
          reverted[movieId].push({
            name,
            status: 'watching',
            ready: false,
            joinedAt: new Date().toISOString()
          });
        }
        return reverted;
      });
    }
  };

  const toggleReady = async (movieId, name) => {
    // Get current ready state to toggle
    const currentViewer = movieViewers[movieId]?.find(v => v.name === name);
    const newReadyState = !currentViewer?.ready;
    
    // Update local state immediately for responsive UI
    setMovieViewers(prev => ({
      ...prev,
      [movieId]: (prev[movieId] || []).map(viewer => 
        viewer.name === name ? { ...viewer, ready: newReadyState } : viewer
      )
    }));

    // Persist ready state to Supabase
    try {
      const { saveMovieViewer } = await import('./services/reviewService');
      const status = newReadyState ? 'ready' : 'watching';
      const result = await saveMovieViewer(movieId, name, status);
      if (result.error) {
        console.error('❌ Failed to persist ready state:', result.error);
      } else {
        console.log('✅ Ready state persisted to Supabase:', movieId, name, status);
      }
    } catch (error) {
      console.error('❌ Error persisting ready state:', error);
    }
  };

  // Returns { ok } so the modal can keep the form open on failure. This used
  // to be fire-and-forget: the optimistic insert below was never reverted and
  // the save's own fallback reported success, so a review that never reached
  // the database sat on screen looking saved until the next reload.
  const submitReview = async (movieId, review) => {
    const reviewWithDate = {
      ...review,
      reviewDate: review.reviewDate || new Date().toISOString()
    };

    // Optimistic, so it appears at once - and reverted below if it did not save.
    setMovieReviews(prev => ({
      ...prev,
      [movieId]: [...(prev[movieId] || []), reviewWithDate]
    }));
    const revert = () => setMovieReviews(prev => ({
      ...prev,
      [movieId]: (prev[movieId] || []).filter(r => r !== reviewWithDate)
    }));

    try {
      const result = await saveMovieReview(movieId, reviewWithDate);
      if (result.error) {
        console.error('❌ Failed to persist review to Supabase:', result.error);
        revert();
        return { ok: false, reason: result.error.message || String(result.error) };
      }
      return { ok: true };
    } catch (error) {
      console.error('❌ Error persisting review:', error);
      revert();
      return { ok: false, reason: error.message };
    }
  };

  // Freddy Krueger countdown messages (counting up)
  const getFreddyMessage = (seconds) => {
    const messages = {
      1: "",
      2: "Freddy's coming for you",
      3: "",
      4: "Better lock your door",
      5: "",
      6: "Grab a crucifix",
      7: "",
      8: "Gonna stay up late", 
      9: "",
      10: "Never sleep again"
    };
    return messages[seconds] || "";
  };

  // Ask the chat channel for reviews of a film people have now seen. Awaited,
  // like createMovieChannel and unlike the countdown notifications: an admin
  // pressed a button and is owed an answer.
  const sendReviewPrompt = async (movieId) => {
    if (!isAdmin) return { sent: false, reason: 'not an admin' };

    const title = findMovieById(movieId)?.title;
    if (!title) return { sent: false, reason: 'movie not found' };

    // movieId becomes a deep link back into the review form.
    return notifyChat('review_prompt', { title, movieId });
  };

  // Where the movie's chat channel is, if it has one. Read-only and open to any
  // signed-in member, not just admins - everyone needs the way in, and this
  // creates nothing and reveals only a channel they can already see in Discord.
  const lookupMovieChannel = async (movieId) => {
    const title = findMovieById(movieId)?.title;
    if (!title) return { sent: false, reason: 'movie not found' };

    return notifyChat('find_channel', { title });
  };

  // Open the movie's chat channel ahead of the screening. Kept separate from
  // startCountdown so an admin can create the room early and let people gather
  // in it; startCountdown then finds that channel rather than making another.
  // Unlike the fire-and-forget countdown notifications, this one is awaited -
  // the admin clicked a button and is owed an answer.
  const createMovieChannel = async (movieId) => {
    if (!isAdmin) return { sent: false, reason: 'not an admin' };

    const title = findMovieById(movieId)?.title;
    if (!title) return { sent: false, reason: 'movie not found' };

    return notifyChat('create_channel', { title });
  };

  // Synchronized countdown functionality
  const startCountdown = async (movieId) => {
    if (!isAdmin) return; // Only admins can start countdown
    
    console.log('🚀 Admin starting synchronized countdown for:', movieId);
    
    // Clear any existing local countdown interval
    if (countdownInterval) {
      clearInterval(countdownInterval);
      setCountdownInterval(null);
    }
    
    // Start synchronized countdown in database
    const currentUser = getCurrentUser();
    const userId = currentUser?.id || null;
    
    const { error } = await startSynchronizedCountdown(movieId, userId);
    if (error) {
      console.error('❌ Failed to start synchronized countdown:', error);
      return;
    }
    
    console.log('✅ Synchronized countdown started, setting up local timer...');

    // Announce it in the festival chat. Deliberately not awaited: the countdown
    // must start on time whether or not chat is reachable, and notifyChat never
    // throws. Server side this finds or creates the movie's own channel, opens
    // the countdown there, and drops a pointer in the main channel.
    const movieTitle = findMovieById(movieId)?.title || 'a movie';

    // Identifies the chat message the ticks below edit. Populated when the
    // start call returns; ticks that fire before then simply carry nulls and
    // are ignored server side, which is why nothing here is awaited.
    let chat = { messageId: null, channelId: null };
    notifyChat('countdown_start', {
      title: movieTitle,
      seconds: COUNTDOWN_SECONDS
    }).then((r) => {
      chat = { messageId: r.messageId, channelId: r.channelId };
      if (r.channelCreated) {
        console.log('📺 Created a chat channel for', movieTitle);
      }
    });

    // Set up local interval to update the database countdown
    let currentSecond = 1;
    setCountdown({ movieId, seconds: currentSecond, phase: 'counting' });
    
    const interval = setInterval(async () => {
      currentSecond++;
      
      console.log('⏰ Countdown tick:', currentSecond);
      
      // Update local state immediately
      if (currentSecond > 10) {
        // Clear interval and show GOOOO!
        clearInterval(interval);
        setCountdownInterval(null);
        
        console.log('🎬 Countdown finished, showing GOOOO!');
        setCountdown({ movieId, seconds: 10, phase: 'goooo' });

        // Resolve the chat countdown in place rather than posting a second line.
        notifyChat('countdown_go', {
          title: movieTitle,
          messageId: chat.messageId,
          channelId: chat.channelId
        });
        
        // Update database to GOOOO phase (async, non-blocking)
        updateCountdownState(movieId, 10, 'goooo').then(() => {
          console.log('✅ Updated to GOOOO phase');
        }).catch(error => {
          console.error('❌ Failed to update to GOOOO phase:', error);
        });
        
        // Show GOOOO! message for 2 seconds, then stop countdown
        setTimeout(async () => {
          try {
            await stopSynchronizedCountdown(movieId);
            console.log('✅ Countdown stopped');
            setCountdown(null);
            setMovieStatus(movieId, 'playing');
          } catch (error) {
            console.error('❌ Failed to stop countdown:', error);
            // Fallback: still clear local countdown and set movie status
            setCountdown(null);
            setMovieStatus(movieId, 'playing');
          }
        }, 2000);
        
        return;
      }
      
      // Update local state immediately
      setCountdown({ movieId, seconds: currentSecond, phase: 'counting' });

      // Tick the chat message in step. One message edited in place, not a new
      // one each second. Fire-and-forget so a slow edit never delays the timer.
      //
      // Held back until countdown_start has answered. That call is deliberately
      // not awaited so the timer starts on time, which means the first few
      // ticks would otherwise carry no message to edit - and a tick with
      // nothing to edit is a tick that gets posted somewhere it does not
      // belong. The server refuses them too; this just avoids the round trip.
      if (chat.messageId) {
        notifyChat('countdown_tick', {
          title: movieTitle,
          second: currentSecond,
          seconds: COUNTDOWN_SECONDS,
          messageId: chat.messageId,
          channelId: chat.channelId
        });
      }

      // Update database asynchronously (non-blocking)
      updateCountdownState(movieId, currentSecond, 'counting').then(() => {
        console.log('✅ Database updated to second:', currentSecond);
      }).catch(error => {
        console.error('❌ Failed to update countdown second:', currentSecond, error);
      });
    }, 1000);
    
    setCountdownInterval(interval);
  };

  // Utility function for manual subscription management (if needed)
  // eslint-disable-next-line no-unused-vars -- dead code, kept for now
  const unsubscribeFromMovieCountdown = useCallback((movieId) => {
    const subscription = countdownSubscriptions.get(movieId);
    if (subscription) {
      console.log('🔇 Unsubscribing from countdown for movie:', movieId);
      unsubscribeFromCountdownChanges(subscription);
      setCountdownSubscriptions(prev => {
        const newMap = new Map(prev);
        newMap.delete(movieId);
        return newMap;
      });
    }
  }, [countdownSubscriptions]);
  
  const getMovieReviews = (movieId) => {
    return movieReviews[movieId] || [];
  };

  const getMovieReviewAggregate = (movieId) => {
    const reviews = movieReviews[movieId] || [];
    if (reviews.length === 0) return null;
    
    const totalOverall = reviews.reduce((sum, r) => sum + r.overallRating, 0);
    const totalScariness = reviews.reduce((sum, r) => sum + r.scarinessRating, 0);
    
    return {
      avgOverall: Math.round((totalOverall / reviews.length) * 10) / 10,
      avgScariness: Math.round((totalScariness / reviews.length) * 10) / 10,
      reviewCount: reviews.length
    };
  };

  // Default streaming services
  const getDefaultStreamingServices = () => [
    { id: 'netflix', name: 'Netflix', color: 'bg-red-600', icon: '🎬' },
    { id: 'amazon', name: 'Amazon Prime', color: 'bg-blue-600', icon: '📺' },
    { id: 'disney', name: 'Disney+', color: 'bg-blue-800', icon: '🏰' },
    { id: 'hulu', name: 'Hulu', color: 'bg-green-600', icon: '🎭' },
    { id: 'hbo', name: 'HBO Max', color: 'bg-purple-700', icon: '👑' },
    { id: 'apple', name: 'Apple TV+', color: 'bg-gray-800', icon: '🍎' },
    { id: 'paramount', name: 'Paramount+', color: 'bg-blue-500', icon: '⭐' },
    { id: 'peacock', name: 'Peacock', color: 'bg-purple-600', icon: '🦚' },
    { id: 'showtime', name: 'Showtime', color: 'bg-red-800', icon: '🎪' },
    { id: 'starz', name: 'Starz', color: 'bg-black', icon: '✨' },
    { id: 'free', name: 'Free (Tubi/Crackle)', color: 'bg-green-700', icon: '🆓' },
    { id: 'rent', name: 'Digital Rental', color: 'bg-orange-700', icon: '💰' },
    { id: 'physical', name: 'Physical Media', color: 'bg-gray-700', icon: '💿' },
    { id: 'theater', name: 'In Theaters', color: 'bg-yellow-600', icon: '🎫' }
  ];

  // Archive functionality
  const archiveWeek = async (weekToArchive) => {
    if (!isAdmin) return; // Only admins can archive
    
    try {
      // Import the archive function
      const { archiveScheduledMoviesForWeek } = await import('./services/calendarService');
      const { setMovieStatus } = await import('./services/appDataService');
      
      console.log('🗄️ Archiving week:', weekToArchive);
      
      // Set all movies in this week to "already watched" and archive them in Supabase
      weekToArchive.forEach(date => {
        const dateStr = date.toISOString().split('T')[0];
        if (scheduledMovies[dateStr]) {
          scheduledMovies[dateStr].forEach(async (scheduledMovie) => {
            // Set movie status to watched
            await setMovieStatus(scheduledMovie.movie.id, 'watched');
          });
        }
      });
      
      // Archive the scheduled movies for this week in Supabase
      await archiveScheduledMoviesForWeek(weekToArchive);
      
      // Create and save the archived calendar week entry
      const weekStart = weekToArchive[0];
      const weekEnd = weekToArchive[weekToArchive.length - 1];
      const weekId = `week-${weekStart.toISOString().split('T')[0]}-${weekEnd.toISOString().split('T')[0]}`;
      
      // Simple week number calculation
      const getWeekNumber = (date) => {
        const start = new Date(date.getFullYear(), 0, 1);
        const days = Math.floor((date - start) / (24 * 60 * 60 * 1000));
        return Math.ceil((days + start.getDay() + 1) / 7);
      };
      
      const calendarWeekData = {
        id: weekId,
        title: `Week of ${weekStart.toLocaleDateString()}`,
        start_date: weekStart.toISOString().split('T')[0],
        end_date: weekEnd.toISOString().split('T')[0],
        dates: weekToArchive.map(date => date.toISOString().split('T')[0]),
        year: weekStart.getFullYear(),
        weekNumber: getWeekNumber(weekStart),
        isArchived: true,
        isHidden: false
      };
      
      console.log('💾 Saving archived calendar week:', calendarWeekData);
      
      try {
        const result = await saveCalendarWeek(calendarWeekData);
        console.log('✅ saveCalendarWeek result:', result);
        
        if (result.error) {
          console.error('❌ Error from saveCalendarWeek:', result.error);
          alert('Failed to save calendar week to database: ' + result.error.message);
        } else {
          console.log('✅ Calendar week saved successfully to Supabase');
        }
      } catch (error) {
        console.error('❌ Exception in saveCalendarWeek:', error);
        alert('Failed to save calendar week: ' + error.message);
      }
      
      // Reload scheduled movies to reflect the archived status
      const scheduledData = await loadScheduledMovies(false); // Only active movies for main calendar
      setScheduledMovies(scheduledData);
      
      // Reload archived scheduled movies
      const archivedScheduledData = await loadScheduledMovies(true); // All movies including archived
      setArchivedScheduledMovies(archivedScheduledData);
      
      // Reload archived weeks
      const archivedWeeksData = await loadArchivedCalendarWeeks();
      setArchivedWeeks(archivedWeeksData);
      
      // Remove the week from calendar weeks (since it's now archived)
      setCalendarWeeks(prev => prev.filter(week => 
        !week.every(date => weekToArchive.some(archiveDate => 
          archiveDate.toDateString() === date.toDateString()
        ))
      ));
      
      console.log('✅ Week archived successfully');
    } catch (error) {
      console.error('❌ Error archiving week:', error);
      alert('Failed to archive week. Please try again.');
    }
  };

  // Delete functionality
  const deleteWeek = async (weekToDelete) => {
    if (!isAdmin) return; // Only admins can delete

    const weekKey = weekToDelete.map(date => date.toISOString().split('T')[0]).join('-');
    const weekRange = getWeekRange(weekToDelete);
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to permanently delete the calendar week "${weekRange}"? This will remove all scheduled movies for this week and cannot be undone.`)) {
      return;
    }

    try {
      // Delete scheduled movies from Supabase first
      const dateStrings = weekToDelete.map(date => date.toISOString().split('T')[0]);
      const moviesResult = await deleteScheduledMoviesForWeek(dateStrings);
      
      if (moviesResult.error) {
        console.error('❌ Failed to delete scheduled movies from Supabase:', moviesResult.error);
        alert('Failed to delete scheduled movies from database. Please try again.');
        return;
      }
      
      // Also delete the calendar week record from Supabase
      const weekStart = weekToDelete[0];
      const weekEnd = weekToDelete[weekToDelete.length - 1];
      const weekId = `week-${weekStart.toISOString().split('T')[0]}-${weekEnd.toISOString().split('T')[0]}`;
      
      console.log('🗑️ Deleting calendar week from Supabase:', weekId);
      const weekResult = await deleteCalendarWeek(weekId);
      
      if (weekResult.error) {
        console.error('❌ Failed to delete calendar week from Supabase:', weekResult.error);
        // Don't stop the process - scheduled movies are already deleted
        console.log('⚠️ Continuing with local deletion despite calendar week deletion error');
      } else {
        console.log('✅ Calendar week deleted from Supabase:', weekId);
      }

      // Update local state after successful database deletion
      const updatedScheduledMovies = { ...scheduledMovies };
      weekToDelete.forEach(date => {
        const dateStr = date.toISOString().split('T')[0];
        delete updatedScheduledMovies[dateStr];
      });
      setScheduledMovies(updatedScheduledMovies);

      // Remove the week from calendar weeks
      setCalendarWeeks(prev => prev.filter(week => 
        !week.every(date => weekToDelete.some(deleteDate => 
          deleteDate.toDateString() === date.toDateString()
        ))
      ));

      // Remove the week from viewable weeks set
      setViewableWeeks(prev => {
        const newSet = new Set(prev);
        newSet.delete(weekKey);
        return newSet;
      });

      console.log(`✅ Successfully deleted week "${weekRange}" from schedule and database`);
    } catch (error) {
      console.error('❌ Error deleting week:', error);
      alert('Failed to delete week. Please try again.');
    }

    console.log(`Calendar week deleted: ${weekRange}`);
  };

  // Admin preference handlers
  const handleDefaultTabChange = async (newDefaultTab) => {
    console.log('🔄 Updating admin default tab:', newDefaultTab);
    
    // Update local state immediately for responsive UI
    setDefaultTab(newDefaultTab);
    
    // Save to localStorage for immediate persistence
    localStorage.setItem('admin_default_tab', newDefaultTab);
    
    // Save to Supabase for cross-device sync
    try {
      const result = await saveAdminDefaultTabAppLevel(newDefaultTab);
      if (result.error) {
        console.error('❌ Failed to save admin default tab to Supabase:', result.error);
        // Don't revert local state - localStorage save was successful
      } else {
        console.log('✅ Admin default tab saved to Supabase:', newDefaultTab);
      }
    } catch (error) {
      console.error('❌ Error saving admin default tab to Supabase:', error);
      // Don't revert local state - localStorage save was successful
    }
  };


  const handleCountdownImagesChange = (newImages) => {
    setCountdownImages(newImages);
  };

  const handleThemeChange = async (newTheme) => {
    console.log('🎨 Changing theme to:', newTheme.name);
    
    // Update local state immediately for responsive UI
    setCurrentTheme(newTheme);
    
    if (isAdmin) {
      // Admin users save globally (affects all users)
      try {
        await saveGlobalThemeAppLevel(newTheme.id);
        console.log('✅ Global theme saved to Supabase:', newTheme.name);
      } catch (error) {
        console.error('❌ Failed to save global theme:', error);
        // Fallback to localStorage
        saveThemePreference(newTheme.id);
        console.log('🔄 Fallback: Theme saved to localStorage');
      }
    } else {
      // Non-admin users save locally only
      saveThemePreference(newTheme.id);
      console.log('✅ Local theme preference saved:', newTheme.name);
    }
    
    console.log('✅ Theme changed successfully:', newTheme.name);
  };

  // Star all visible movies function
  const starAllVisibleMovies = async () => {
    if (!isUserAuthenticated) {
      console.warn('⚠️ User must be authenticated to star movies');
      return;
    }

    const currentUser = getCurrentUser();
    const userIdentifier = currentUser?.email || 'anonymous';
    const visibleMovies = getPaginatedMovies();
    const unstarredMovies = visibleMovies.filter(movie => !starredMovies.has(movie.id));
    
    if (unstarredMovies.length === 0) {
      console.log('ℹ️ No unstarred movies to star');
      return;
    }

    console.log(`⭐ Starring ${unstarredMovies.length} movies...`);

    // Update UI immediately for responsive feedback
    const newStarredMovies = new Set(starredMovies);
    unstarredMovies.forEach(movie => newStarredMovies.add(movie.id));
    setStarredMovies(newStarredMovies);

    // Star each movie individually using the existing service
    let successCount = 0;
    let failedMovies = [];

    for (const movie of unstarredMovies) {
      try {
        const result = await toggleStarredMovieService(movie.id, userIdentifier, true);
        if (result.error) {
          console.error(`❌ Failed to star movie ${movie.title}:`, result.error);
          failedMovies.push(movie);
        } else {
          successCount++;
        }
      } catch (error) {
        console.error(`❌ Error starring movie ${movie.title}:`, error);
        failedMovies.push(movie);
      }
    }

    // Report results
    if (failedMovies.length === 0) {
      console.log(`✅ Successfully starred all ${successCount} movies`);
    } else {
      console.warn(`⚠️ Starred ${successCount} movies, failed to star ${failedMovies.length} movies`);
      
      // Revert failed movies in local state
      setStarredMovies(prev => {
        const revertedStarred = new Set(prev);
        failedMovies.forEach(movie => revertedStarred.delete(movie.id));
        return revertedStarred;
      });
    }
  };

  const getAvailableArchiveYears = useCallback(() => {
    console.log('🔍 getAvailableArchiveYears called, archivedWeeks:', archivedWeeks.length, 'weeks');
    const years = new Set();
    archivedWeeks.forEach(archivedWeek => {
      // Use the calendar week start year instead of archive date
      if (archivedWeek.week && archivedWeek.week[0]) {
        const weekStartYear = archivedWeek.week[0].getFullYear();
        years.add(weekStartYear.toString());
      } else {
        console.warn('⚠️ Invalid archived week structure:', archivedWeek);
      }
    });
    const sortedYears = Array.from(years).sort((a, b) => b.localeCompare(a)); // Most recent first
    console.log('✅ Available archive years:', sortedYears);
    return sortedYears;
  }, [archivedWeeks]);

  const getArchivedWeeksForYear = useCallback((year) => {
    console.log('🔍 getArchivedWeeksForYear called for year:', year, 'total weeks:', archivedWeeks.length);
    const filteredWeeks = archivedWeeks.filter(archivedWeek => {
      if (archivedWeek.week && archivedWeek.week[0]) {
        return archivedWeek.week[0].getFullYear().toString() === year;
      } else {
        console.warn('⚠️ Invalid archived week structure in filter:', archivedWeek);
        return false;
      }
    }).sort((a, b) => a.week[0].getTime() - b.week[0].getTime()); // Sort by calendar week start date
    console.log('✅ Filtered weeks for year', year + ':', filteredWeeks.length, 'weeks');
    return filteredWeeks;
  }, [archivedWeeks]);

  const getScheduledMovieForArchivedSlot = (date, timeSlot, archivedWeekData) => {
    // For archived calendars, shift the lookup one day forward to compensate for timezone shift
    const shiftedDate = new Date(date);
    shiftedDate.setDate(date.getDate() + 1);
    
    // Use timezone-agnostic date string conversion to avoid UTC shift
    const year = shiftedDate.getFullYear();
    const month = String(shiftedDate.getMonth() + 1).padStart(2, '0');
    const day = String(shiftedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const dayMovies = archivedWeekData.scheduledMovies[dateStr] || [];
    
    // Debug: Log date conversion for archived movies
    if (dayMovies.length > 0) {
      console.log(`🔍 Debug archived movie lookup: Display date ${date.toDateString()} -> Lookup date ${shiftedDate.toDateString()} (${dateStr}) -> found ${dayMovies.length} movies`);
    }
    
    for (const movie of dayMovies) {
      const movieStartIndex = timeSlots.findIndex(ts => ts.id === movie.timeSlot.id);
      const slotIndex = timeSlots.findIndex(ts => ts.id === timeSlot.id);
      
      if (slotIndex >= movieStartIndex && slotIndex < movieStartIndex + movie.slotsSpanned) {
        return movie;
      }
    }
    return null;
  };

  const isArchivedSlotOccupied = (date, timeSlot, archivedWeekData) => {
    return getScheduledMovieForArchivedSlot(date, timeSlot, archivedWeekData) !== null;
  };

  // Movie collection management
  const handleTMDBImport = async (importedMovies) => {
    console.log('🎬 handleTMDBImport called with', importedMovies.length, 'movies');
    console.log('🎬 Sample imported movie:', importedMovies[0]);
    
    try {
      const { saveMovieCollection } = await import('./services/movieCollectionService');
      
      setAllMovies(prev => {
        const existingIds = new Set(prev.map(movie => movie.id));
        const newMovies = importedMovies.filter(movie => !existingIds.has(movie.id));
        const updatedMovies = [...prev, ...newMovies];
        
        // Save to Supabase
        saveMovieCollection(updatedMovies).then(result => {
          if (result.error) {
            console.error('❌ Failed to save movie collection to Supabase:', result.error);
          } else {
            console.log('✅ Movie collection saved to Supabase:', updatedMovies.length, 'movies');
          }
        });
        
        return updatedMovies;
      });
    } catch (error) {
      console.error('❌ Error importing movies:', error);
    }
  };

  const removeMovieFromCollection = async (movieId) => {
    if (!isAdmin) return; // Only admins can remove movies
    
    try {
      const { saveMovieCollection } = await import('./services/movieCollectionService');
      
      // Remove from allMovies
      setAllMovies(prev => {
        const updatedMovies = prev.filter(movie => movie.id !== movieId);
        
        // Save to Supabase
        saveMovieCollection(updatedMovies).then(result => {
          if (result.error) {
            console.error('❌ Failed to save movie collection to Supabase:', result.error);
          } else {
            console.log('✅ Movie collection updated in Supabase:', updatedMovies.length, 'movies');
          }
        });
        
        return updatedMovies;
      });
      
      // Remove from starred movies
      setStarredMovies(prev => {
        const updated = new Set(prev);
        updated.delete(movieId);
        return updated;
      });
      
      // Remove from scheduled movies
      setScheduledMovies(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(dateStr => {
          updated[dateStr] = updated[dateStr].filter(scheduledMovie => 
            scheduledMovie.movie.id !== movieId
          );
          // Clean up empty date entries
          if (updated[dateStr].length === 0) {
            delete updated[dateStr];
          }
        });
        return updated;
      });
      
      // Remove movie reviews
      setMovieReviews(prev => {
        const updated = { ...prev };
        delete updated[movieId];
        return updated;
      });
      
      // Remove movie viewers
      setMovieViewers(prev => {
        const updated = { ...prev };
        delete updated[movieId];
        return updated;
      });
    
      // Remove movie statuses
      setMovieStatuses(prev => {
        const updated = { ...prev };
        delete updated[movieId];
        return updated;
      });
    } catch (error) {
      console.error('❌ Error removing movie from collection:', error);
    }
  };

  const clearMovieCollection = () => {
    if (!isAdmin) return; // Only admins can clear collection
    
    if (window.confirm('Are you sure you want to remove ALL movies from your collection? This cannot be undone.')) {
      setAllMovies([]);
      setStarredMovies(new Set());
      setScheduledMovies({});
      setMovieReviews({});
      setMovieViewers({});
      setMovieStatuses({});
    }
  };

  // Unarchive functionality
  const unarchiveWeek = (archivedWeekData) => {
    if (!isAdmin) return; // Only admins can unarchive
    
    // Restore the week to calendar weeks
    setCalendarWeeks(prev => {
      // Check if the week is already in the calendar (avoid duplicates)
      const weekExists = prev.some(existingWeek => 
        existingWeek.every(date => 
          archivedWeekData.week.some(archiveDate => 
            archiveDate.toDateString() === date.toDateString()
          )
        )
      );
      
      if (!weekExists) {
        return [...prev, archivedWeekData.week].sort((a, b) => 
          a[0].getTime() - b[0].getTime() // Sort by first day of week
        );
      }
      return prev;
    });
    
    // Restore scheduled movies for this week
    setScheduledMovies(prev => ({
      ...prev,
      ...archivedWeekData.scheduledMovies
    }));
    
    // Remove from archived weeks
    setArchivedWeeks(prev => prev.filter(archived => archived.id !== archivedWeekData.id));
  };

  // Week visibility functionality
  const getWeekKey = (week) => {
    return week.map(date => date.toISOString().split('T')[0]).join('-');
  };

  const toggleWeekViewable = async (weekKey) => {
    if (!isAdmin) return; // Only admins can change visibility
    
    const isCurrentlyVisible = viewableWeeks.has(weekKey);
    const newVisibility = !isCurrentlyVisible;
    
    console.log('🔄 Toggling week visibility:', { weekKey, isCurrentlyVisible, newVisibility });
    
    // Update local state immediately for responsive UI
    setViewableWeeks(prev => {
      const newViewableWeeks = new Set(prev);
      if (newVisibility) {
        newViewableWeeks.add(weekKey);
      } else {
        newViewableWeeks.delete(weekKey);
      }
      return newViewableWeeks;
    });
    
    // Persist to Supabase
    try {
      const { setWeekVisibility } = await import('./services/appDataService');
      const result = await setWeekVisibility(weekKey, newVisibility);
      if (result.error) {
        console.error('❌ Failed to persist week visibility:', result.error);
        // Revert local state on error
        setViewableWeeks(prev => {
          const revertedWeeks = new Set(prev);
          if (isCurrentlyVisible) {
            revertedWeeks.add(weekKey);
          } else {
            revertedWeeks.delete(weekKey);
          }
          return revertedWeeks;
        });
      } else {
        console.log('✅ Week visibility persisted to Supabase:', weekKey, newVisibility);
      }
    } catch (error) {
      console.error('❌ Error persisting week visibility:', error);
      // Revert local state on error
      setViewableWeeks(prev => {
        const revertedWeeks = new Set(prev);
        if (isCurrentlyVisible) {
          revertedWeeks.add(weekKey);
        } else {
          revertedWeeks.delete(weekKey);
        }
        return revertedWeeks;
      });
    }
  };

  const isWeekViewable = (week) => {
    const weekKey = getWeekKey(week);
    return viewableWeeks.has(weekKey);
  };

  // Admins see every week; everyone else sees only the public ones.
  //
  // Memoised because the non-admin branch filters, which builds a NEW array on
  // every render. ViewerTab homes the calendar on today whenever this prop
  // changes identity, so an unmemoised copy sent it back to today on every
  // state change - including the one from closing a movie's modal. Admins were
  // unaffected, because their branch returns the existing array untouched,
  // which is why the bug only ever showed up in other people's hands.
  const publicWeeks = useMemo(
    () => (isAdmin ? calendarWeeks : calendarWeeks.filter(week => isWeekViewable(week))),
    // isWeekViewable is rebuilt each render but only reads viewableWeeks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, calendarWeeks, viewableWeeks]
  );

  // Ensure non-admin users can only access viewer, reviews, survey, and settings tabs
  useEffect(() => {
    if (!isAdmin && !['viewer', 'reviews', 'survey', 'settings'].includes(activeTab)) {
      console.log('Non-admin user on invalid tab, switching to default tab:', activeTab, '->', defaultTab);
      setActiveTab(defaultTab);
    }
  }, [isAdmin, activeTab, defaultTab]);

  return (
    // data-theme lets a theme restyle the interface itself from CSS. Themes
    // only ever reached the background before this, and there are ~530
    // hardcoded colour classes across the components, so per-theme chrome has
    // to be layered on from outside rather than threaded through every one.
    <div
      data-theme={currentTheme?.id}
      className="app-shell min-h-screen text-white p-6 relative overflow-hidden"
    >
      {/* Themed Animated Background. Purely decorative, so a broken theme must
          never cost more than the decoration - and its fallback renders
          nothing rather than an error panel behind the whole interface. */}
      <ErrorBoundary label="The background" silent>
        <AnimatedBackground theme={currentTheme} />
      </ErrorBoundary>

      {/* Projection chrome. Empty for every theme except the drive-in, which
          styles them in App.css; they are inert elsewhere. */}
      <div className="app-letterbox app-letterbox--top" aria-hidden="true" />
      <div className="app-letterbox app-letterbox--bottom" aria-hidden="true" />
      <div className="app-grain" aria-hidden="true" />
      {/* Camcorder HUD for found-footage themes. Renders nothing otherwise. */}
      <VhsOverlay active={Boolean(currentTheme?.chrome?.vhs)} />

      {/* Main UI Content - Layered for translucent effects. Hidden as a
          whole by the toggle below: faded out and unclickable, still in the
          tree so nothing is lost. */}
      <div className={`app-content relative${uiHidden ? ' app-content--hidden' : ''}`} aria-hidden={uiHidden || undefined}>

      {/* Global Countdown Display - Visible to All Users */}
      {countdown && (
        <div className={`fixed bg-black/90 backdrop-blur-sm ${
          showViewerModal && countdown.phase === 'goooo' && countdownImages.length > 0
            ? 'z-[60]' // Higher z-index when overlaying modal
            : 'z-50' // Normal z-index for top banner
        } ${
          showViewerModal && countdown.phase === 'goooo' && countdownImages.length > 0
            ? 'inset-0 flex items-center justify-center' // Full screen overlay when modal is open and showing image
            : 'top-0 left-0 right-0' // Top banner otherwise
        }`}>
          <div className={`text-center py-4 px-6 ${
            countdown.phase === 'goooo' 
              ? 'bg-red-900 animate-pulse' 
              : 'bg-red-900 animate-bounce'
          } ${
            showViewerModal && countdown.phase === 'goooo' && countdownImages.length > 0
              ? 'rounded-lg shadow-2xl border-4 border-red-500 max-w-2xl mx-4' // Modal overlay styling
              : '' // Top banner styling (default)
          }`}>
            {countdown.phase === 'goooo' ? (
              <div className="space-y-3">
                <div className="text-4xl font-bold text-red-300 animate-pulse">
                  🎬 GOOOO! 🎬
                </div>
                <div className="text-lg text-red-200">
                  The movie is starting NOW!
                </div>
                {/* Random countdown image */}
                {countdownImages.length > 0 && (() => {
                  const randomImage = countdownImages[Math.floor(Math.random() * countdownImages.length)];
                  return (
                    <div className="flex justify-center">
                      <img
                        src={randomImage.data}
                        alt="Countdown surprise"
                        className={`object-contain rounded-lg border-2 border-red-500 shadow-lg animate-pulse ${
                          showViewerModal 
                            ? 'max-h-64 max-w-lg' // Larger when overlaying modal
                            : 'max-h-48 max-w-sm' // Smaller when at top
                        }`}
                      />
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="text-4xl font-bold text-red-400">
                  {countdown.seconds}
                </div>
                {getFreddyMessage(countdown.seconds) && (
                  <div className="text-xl text-red-300 font-semibold italic">
                    {getFreddyMessage(countdown.seconds)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className={`max-w-7xl mx-auto ${
        countdown && !(showViewerModal && countdown.phase === 'goooo' && countdownImages.length > 0) 
          ? 'pt-20' // Add padding only when countdown is at top (not overlaying modal)
          : ''
      }`}>
        <div className="relative z-10">
          <TabNavigation 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            isAdmin={isAdmin}
            setIsAdmin={handleAdminChange}
            currentTheme={currentTheme}
          />
        </div>

        {/* One boundary around every tab panel, deliberately placed BELOW the
            navigation: a tab that throws leaves the nav intact, so you can move
            to one that works instead of staring at a blank page. Keyed on the
            tab so switching remounts it and clears the error. */}
        <ErrorBoundary key={activeTab} label={TAB_LABELS[activeTab] || 'This tab'}>

        {/* Schedule Tab - Admin Only */}
        {activeTab === 'schedule' && isAdmin && (
          <div className="space-y-6">
            {/* Calendar Setup */}
            <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
              <h2 className="text-xl font-bold text-orange-300 mb-4">📅 Schedule Setup</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-orange-300 text-sm font-bold mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-orange-300 text-sm font-bold mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-orange-300 text-sm font-bold mb-2">
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                    className="w-full bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-3 py-2"
                  >
                    {timezones.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                <button
                  onClick={generateCalendar}
                  disabled={!startDate || !endDate}
                    className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                >
                    Generate Calendar
                </button>
              </div>
            </div>
                  </div>
                  
            {/* Calendar and Movie Library */}
            {calendarWeeks.length > 0 && (
            <div className="flex gap-6" style={{ height: 'calc(100vh - 26rem)' }}>
              {/* Movie Library */}
              <div className="w-80 flex-shrink-0 flex flex-col">
                <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-4 flex flex-col" style={{ height: '100%' }}>
                  <h2 className="text-lg font-bold mb-4 text-orange-300">⚰️ Starred Movies</h2>
                  <p className="text-sm text-orange-200 mb-4">
                      Drag movies to the calendar to schedule them. Star movies in the Collection tab to add them here.
                    </p>
                    
                    <div className="flex-1 overflow-y-auto space-y-3">
                      {getAvailableMovies().map(movie => (
                        <MovieCard
                          key={movie.id}
                          movie={movie}
                          onDragStart={handleDragStart}
                          isDraggable={true}
                          isStarred={starredMovies.has(movie.id)}
                          showStarButton={false}
                          reviewAggregate={getMovieReviewAggregate(movie.id)}
                        />
                      ))}
                      
                      {getAvailableMovies().length === 0 && (
                        <div className="text-center py-8 text-orange-400">
                          <p className="mb-2">No starred movies yet!</p>
                          <p className="text-sm">Go to the Collection tab to star some movies.</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                    </div>
                    
                {/* Calendar */}
                <Calendar
                  calendarWeeks={calendarWeeks}
                  timeSlots={timeSlots}
                  scheduledMovies={scheduledMovies}
                  getScheduledMovieForSlot={getScheduledMovieForSlot}
                  isSlotOccupied={isSlotOccupied}
                  handleDragOver={handleDragOver}
                  handleDrop={handleDrop}
                  removeScheduledMovie={removeScheduledMovie}
                  setDraggedMovie={setDraggedMovie}
                  getStreamingServiceById={getStreamingServiceById}
                  isAdmin={isAdmin}
                  onArchiveWeek={archiveWeek}
                  onDeleteWeek={deleteWeek}
                  getWeekKey={getWeekKey}
                  isWeekViewable={isWeekViewable}
                  toggleWeekViewable={toggleWeekViewable}
                />
                  </div>
                )}
          </div>
        )}

        {/* Collection Tab - Admin Only */}
        {activeTab === 'collection' && isAdmin && (
          <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-orange-300 mb-2">🎬 Your Cursed Movie Collection</h2>
                  <p className="text-orange-200">Browse, search, and manage your spooky film library</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowTMDBUrlImporter(true)}
                    className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                  >
                    🔗 Add from URL
                  </button>
                  <button
                    onClick={() => setShowTMDBListImporter(true)}
                    className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                  >
                    📋 Import TMDB Lists
                  </button>
                  <button
                    onClick={() => setShowPlaceholderCreator(true)}
                    className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                    title="Create a custom movie entry"
                  >
                    📝 Create Placeholder
                  </button>
                  <button
                    onClick={clearMovieCollection}
                    className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                    title="Remove all movies from collection"
                  >
                    🗑️ Clear All
                  </button>
                </div>
              </div>
              
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={handleSearchChange}
              placeholder="🔍 Search movies by title or year..."
              sortAlphabetically={sortAlphabetically}
              setSortAlphabetically={handleSortChange}
              onStarAllVisible={starAllVisibleMovies}
              visibleMoviesCount={getPaginatedMovies().filter(movie => !starredMovies.has(movie.id)).length}
              showStarAllButton={isUserAuthenticated}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {getPaginatedMovies().map(movie => (
                <div key={movie.id} className="cursor-pointer">
                  <EnhancedMovieCard
                    movie={movie}
                    onDragStart={() => {}}
                    isDraggable={false}
                    isStarred={starredMovies.has(movie.id)}
                    onToggleStar={toggleStarredMovie}
                    showStarButton={true}
                    reviewAggregate={getMovieReviewAggregate(movie.id)}
                    onClick={() => openMovieModal(movie)}
                    showFullDetails={true}
                    onRemove={removeMovieFromCollection}
                    showRemoveButton={true}
                  />
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {getTotalPages() > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={getTotalPages()}
                  onPageChange={handlePageChange}
                  totalItems={getFilteredAndSortedMovies().length}
                  itemsPerPage={moviesPerPage}
                  itemName="movies"
                />
              </div>
            )}
          </div>
        )}

                {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <ReviewsTab 
            allMovies={allMovies}
            movieReviews={movieReviews}
            onMovieClick={openViewerModal}
            getStreamingServiceById={getStreamingServiceById}
            loadCalendarData={loadCalendarData}
            calendarDataLoading={calendarDataLoading}
          />
        )}

                {/* Viewer Tab */}
        {activeTab === 'viewer' && (
          <ViewerTab
            calendarWeeks={publicWeeks}
            scheduledMovies={scheduledMovies}
            timeSlots={timeSlots}
            timezone={timezone}
            getScheduledMovieForSlot={getScheduledMovieForSlot}
            isSlotOccupied={isSlotOccupied}
            getWeekRange={getWeekRange}
            onMovieClick={openViewerModal}
            archivedWeeks={archivedWeeks}
            getAvailableArchiveYears={getAvailableArchiveYears}
            getArchivedWeeksForYear={getArchivedWeeksForYear}
            getScheduledMovieForArchivedSlot={getScheduledMovieForArchivedSlot}
            isArchivedSlotOccupied={isArchivedSlotOccupied}
            isAdmin={isAdmin}
            onUnarchiveWeek={unarchiveWeek}
            allMovies={allMovies}
            movieReviews={movieReviews}
            getStreamingServiceById={getStreamingServiceById}
            loadCalendarData={loadCalendarData}
            calendarDataLoading={calendarDataLoading}
            isUserAuthenticated={isUserAuthenticated}
          />
        )}

        {/* Survey Tab - Available to All Users */}
        {activeTab === 'survey' && (
          <SurveyTab
            allMovies={allMovies}
            isAdmin={isAdmin}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="relative z-10">
            <SettingsTab
            isAdmin={isAdmin}
            allMovies={allMovies}
            onBatchEnrichMovies={() => {
              // TODO: Implement batch enrichment
              console.log('Batch enrichment not yet implemented');
            }}
            defaultTab={defaultTab}
            onDefaultTabChange={handleDefaultTabChange}
            countdownImages={countdownImages}
            onCountdownImagesChange={handleCountdownImagesChange}
            currentTheme={currentTheme}
            onThemeChange={handleThemeChange}
          />
          </div>
        )}

        </ErrorBoundary>

        {/* Modals. Wrapped separately - a modal is rendered alongside the tabs
            rather than inside one, so a crash here would otherwise still take
            the page with it. */}
        <ErrorBoundary label="That dialog">
        <MovieModal
          movie={selectedMovie}
          isVisible={showMovieModal}
          onClose={closeMovieModal}
          isStarred={selectedMovie ? starredMovies.has(selectedMovie.id) : false}
          onToggleStar={toggleStarredMovie}
          onUpdateStreamingServices={updateMovieStreamingServices}
        />

        <ViewerModal
          scheduledMovie={selectedViewerMovie}
          isVisible={showViewerModal}
          onClose={closeViewerModal}
          isAdmin={isAdmin}
          movieStatus={selectedViewerMovie && selectedViewerMovie.movie ? getMovieStatus(selectedViewerMovie.movie.id) : 'coming'}
          onStatusChange={setMovieStatus}
          viewers={selectedViewerMovie && selectedViewerMovie.movie ? (movieViewers[selectedViewerMovie.movie.id] || []) : []}
          userName={userName}
          onJoinWatching={joinWatching}
          onLeaveWatching={leaveWatching}
          onToggleReady={toggleReady}
          reviews={selectedViewerMovie && selectedViewerMovie.movie ? getMovieReviews(selectedViewerMovie.movie.id) : []}
          onOpenReviewModal={openReviewModal}
          streamingServices={getDefaultStreamingServices()}
          countdown={countdown}
          onStartCountdown={startCountdown}
          onCreateChannel={createMovieChannel}
          onLookupChannel={lookupMovieChannel}
          onSendReviewPrompt={sendReviewPrompt}
          getFreddyMessage={getFreddyMessage}
        />

        <ReviewModal
          movie={reviewingMovieId ? findMovieById(reviewingMovieId) : null}
          isVisible={showReviewModal}
          onClose={closeReviewModal}
          onSubmitReview={submitReview}
        />

        <TMDBListImporter
          isVisible={showTMDBListImporter}
          onClose={() => setShowTMDBListImporter(false)}
          onImportMovies={handleTMDBImport}
        />

        <TMDBUrlImporter
          isVisible={showTMDBUrlImporter}
          onClose={() => setShowTMDBUrlImporter(false)}
          onAddMovie={handleTMDBImport}
        />

        <PlaceholderMovieCreator
          isVisible={showPlaceholderCreator}
          onClose={() => setShowPlaceholderCreator(false)}
          onAddMovie={handleTMDBImport}
        />
        </ErrorBoundary>
      </div>
      
      </div> {/* End Main UI Content */}

      {/* Outside the content, so it survives the hiding. */}
      <HideInterfaceToggle hidden={uiHidden} onToggle={toggleUiHidden} />
    </div>
  );
}
